import { InputField } from '../../../molecules/input-field/input-field.molecule';
import { LabelV2 } from '../../../atoms/v2/label/label-v2.atom';
import { InputMaskV2 } from '../../../atoms/v2/input/input-v2.atom';
import React from 'react';
import { SectionTitleV2 } from '../../../atoms/v2/section-title/section-title.atom';
import * as S from './checkout-form.organism.style';
import { useTranslation } from 'react-i18next';
import { FormDataShape } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { toTitleCase } from '../../../../common/utils/to-title-case';
import { CountryData } from '../../../../common/hooks/useCountries';
import { Checkout } from '../../../../common/types/checkout';
import { documentMasks } from '../../../../common/utils/document-masks';

type Props = {
  formData: FormDataShape;
  setFormData: (field: keyof FormDataShape, value: string) => void;
  selectedCountry: CountryData;
  setSelectedCountry: (country: CountryData) => void;
  showCountryPicker: boolean;
  setShowCountryPicker: (show: boolean) => void;
  countrySearch: string;
  setCountrySearch: (search: string) => void;
  filteredCountries: CountryData[];
  checkout: Checkout;
};

export const CheckoutForm: React.FC<Props> = ({
  formData,
  setFormData,
  selectedCountry,
  setSelectedCountry,
  showCountryPicker,
  setShowCountryPicker,
  countrySearch,
  setCountrySearch,
  filteredCountries,
  checkout,
}) => {
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const MAX_NAME_LENGTH = 80;
  const MAX_EMAIL_LENGTH = 60;

  const sanitizeEmail = (value: string) => {
    return value.slice(0, 60);
  };
  const { t } = useTranslation();
  return (
    <S.FormContainer>
      <SectionTitleV2>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
          />
        </svg>
        <span>{t('yourData')}</span>
      </SectionTitleV2>

      <S.FormFields>
        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="name"
          label={t('name')}
          placeholder={t('fillYourName')}
          value={formData.fullName}
          maxLength={MAX_NAME_LENGTH}
          onChange={(e) => {
            if (e.target.value.length >= MAX_NAME_LENGTH) {
              setNameError(t('nameMustBeLessThan', { max: 80 }));
            } else {
              setNameError(null);
            }

            setFormData('fullName', toTitleCase(e?.target?.value));
          }}
          suppressAnalytics={true}
        />
        {nameError && (
          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {nameError}
          </span>
        )}

        <InputField
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
              ?.background
          }
          id="email"
          label="E-mail"
          type="email"
          placeholder={t('fillYourEmail')}
          value={formData.email}
          maxLength={MAX_EMAIL_LENGTH}
          onChange={(e) => {
            const sanitized = sanitizeEmail(e.target.value);

            if (sanitized.length >= MAX_EMAIL_LENGTH) {
              setEmailError(
                t('emailMustBeLessThan', { max: MAX_EMAIL_LENGTH }),
              );
            } else {
              setEmailError(null);
            }

            setFormData('email', toTitleCase(sanitized));
          }}
          suppressAnalytics={true}
        />

        {emailError && (
          <span style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {emailError}
          </span>
        )}
        <S.GridContainer>
          <S.PhoneInputWrapper>
            <LabelV2 htmlFor="phone">{t('cellphone')}</LabelV2>
            <S.PhoneInputContainer>
              <S.CountrySelector
                onClick={() => setShowCountryPicker(!showCountryPicker)}
              >
                <span style={{ fontSize: '20px' }}>
                  <S.Brand src={selectedCountry?.flagUrl} alt="" />
                </span>
                <span style={{ fontSize: '14px', marginLeft: '4px' }}>
                  {selectedCountry?.dialCode}
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  style={{ marginLeft: '4px' }}
                >
                  <path d="M2 4l4 4 4-4H2z" />
                </svg>
              </S.CountrySelector>

              <InputMaskV2
                color={
                  checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                    ?.background
                }
                id="phoneNumber"
                type="tel"
                mask={selectedCountry?.mask}
                maskChar=""
                placeholder={t('fillYourPhone')}
                value={formData.phoneNumber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData('phoneNumber', e.target.value)
                }
                style={{ paddingLeft: '120px' }}
                data-hj-suppress
              />

              {showCountryPicker && (
                <S.CountryDropdown>
                  <S.CountrySearchInput
                    type="text"
                    placeholder={t('searchCountryOrCode')}
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                  />
                  <S.CountryList>
                    {filteredCountries.map((country, index: number) => (
                      <S.CountryItem
                        key={index}
                        onClick={() => {
                          setSelectedCountry(country);
                          setShowCountryPicker(false);
                          setCountrySearch('');
                        }}
                      >
                        <span style={{ fontSize: '20px' }}>
                          <S.Brand src={country?.flagUrl} alt="" />{' '}
                        </span>
                        <span style={{ marginLeft: '8px' }}>
                          {country.name}
                        </span>
                        <span
                          style={{
                            marginLeft: 'auto',
                            color: '#9ca3af',
                            fontSize: '14px',
                          }}
                        >
                          {country.dialCode}
                        </span>
                      </S.CountryItem>
                    ))}
                  </S.CountryList>
                </S.CountryDropdown>
              )}
            </S.PhoneInputContainer>
          </S.PhoneInputWrapper>
        </S.GridContainer>

        <div>
          <LabelV2 htmlFor="cpf">{t('document')}</LabelV2>
          <InputMaskV2
            color={
              checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                ?.background
            }
            id="cpf"
            type="text"
            mask={documentMasks[checkout.paisEnum]}
            maskChar=""
            placeholder={t('fillYourDocument')}
            value={formData.cpf}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFormData('cpf', e.target.value)
            }
            data-hj-suppress
          />
        </div>

        <S.InfoLink
          color={
            checkout?.checkoutConfig?.config?.checkoutData?.securePurchase
              ?.color
          }
        >
          {t('whyWeAskThis')}
        </S.InfoLink>
      </S.FormFields>
    </S.FormContainer>
  );
};
